/*
 * Keeps open tabs up to date after a frontend deployment by fetching index.html
 * with cache-busting options and comparing its hashed JS/CSS bundle paths under
 * /static/ with those referenced by the current page. Reloads on a mismatch.
 *
 * Checks run when the page becomes visible, gains focus, is restored, or changes
 * routes. A shared timestamp limits repeated requests; route checks are skipped
 * when a form submission is detected. Failed requests leave the page unchanged.
 */
import { useEffect, useRef } from 'react'
import { useLocation, useNavigation } from 'react-router-dom'

const MIN_INTERVAL_MS = 10 * 1000 // Page events: 10s minimum.
const NAVIGATION_CHECK_INTERVAL_MS = 5 * 60 * 1000 // Route changes: 5min default.

let lastCheckAt = 0

const assetUrls = (root) =>
    [...root.querySelectorAll('script[src], link[rel="stylesheet"][href]')]
        .map(el => el.getAttribute('src') || el.getAttribute('href'))
        .filter(u => u && u.includes('/static/'))
        .map(u => new URL(u, window.location.origin).pathname)
        .sort()
        .join('|')

// On tab restore, fetch a fresh index.html (bypassing every cache) and compare
// the hashed bundle URLs against the ones this page loaded. Reload on mismatch.
export async function checkForNewVersion() {
    lastCheckAt = Date.now()
    try {
        const res = await fetch(`${process.env.PUBLIC_URL}/index.html?_=${Date.now()}`, {
            cache: 'no-store',
            credentials: 'same-origin',
        })
        if (!res.ok) return false
        const fresh = new DOMParser().parseFromString(await res.text(), 'text/html')
        const current = assetUrls(document)
        const latest = assetUrls(fresh)
        console.debug('[version-check]', { current, latest })
        if (latest && current !== latest) {
            console.info('[version-check] new frontend version detected, reloading')
            window.location.reload()
            return true
        }
    } catch (e) {
        console.debug('[version-check] failed', e)
    }
    return false
}

export function useReloadOnStaleVersion() {
    useEffect(() => {
        const run = () => {
            if (document.hidden || Date.now() - lastCheckAt < MIN_INTERVAL_MS) return
            checkForNewVersion()
        }
        document.addEventListener('visibilitychange', run)
        window.addEventListener('focus', run)
        window.addEventListener('pageshow', run)
        return () => {
            document.removeEventListener('visibilitychange', run)
            window.removeEventListener('focus', run)
            window.removeEventListener('pageshow', run)
        }
    }, [])
}

// Must render inside the router. When the user navigates to a new route and the
// last check is older than the interval, check for a new version. Form
// submissions (navigation.formMethod set) are never interrupted.
export function useVersionCheckOnNavigate(intervalMs = NAVIGATION_CHECK_INTERVAL_MS) {
    const location = useLocation()
    const navigation = useNavigation()
    const submitting = useRef(false)
    submitting.current = Boolean(navigation.formMethod)

    useEffect(() => {
        if (submitting.current) return
        if (Date.now() - lastCheckAt < intervalMs) return
        checkForNewVersion()
    }, [location.pathname, intervalMs])
}
